import React, { useState, useEffect } from 'react';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { products } from '../data/mock';

const ProductCard = ({ product }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const hasSlider = product.slider && product.slider.length > 0;
  const slides = hasSlider ? product.slider : [{ name: product.name, image: product.image }];

  useEffect(() => {
    if (!hasSlider) return;
    
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 3000);

    return () => clearInterval(interval);
  }, [hasSlider, slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  return (
    <div className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-300 border border-gray-100">
      {/* Product Image with Slider */}
      <div className="relative h-64 overflow-hidden">
        <img
          src={slides[currentSlide].image}
          alt={slides[currentSlide].name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
        />
        
        {/* Category Badge */}
        <div className="absolute top-4 left-4">
          <span className="bg-green-700 text-white px-4 py-1.5 rounded-full text-sm font-semibold">
            {product.category}
          </span>
        </div>

        {/* Slider Controls - Only show if product has slider */}
        {hasSlider && (
          <>
            <button
              onClick={prevSlide}
              className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all opacity-0 group-hover:opacity-100"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={nextSlide}
              className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white text-gray-800 rounded-full p-2 shadow-lg transition-all opacity-0 group-hover:opacity-100"
            >
              <ChevronRight size={20} />
            </button>

            {/* Slide Name Overlay */}
            <div className="absolute bottom-4 left-4 right-4">
              <div className="bg-black/60 backdrop-blur-sm text-white px-4 py-2 rounded-lg">
                <p className="text-sm font-semibold">{slides[currentSlide].name}</p>
              </div>
            </div>

            {/* Slide Indicators */}
            <div className="absolute bottom-4 right-4 flex space-x-2">
              {slides.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentSlide ? 'bg-white w-6' : 'bg-white/50'
                  }`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Product Info */}
      <div className="p-6">
        <h3 className="text-2xl font-bold text-amber-950 mb-3">{product.name}</h3>
        <p className="text-gray-600 leading-relaxed mb-5">{product.description}</p>
        <button
          data-testid={`request-quote-${product.id}`}
          onClick={() => {
            const element = document.querySelector('#contact');
            if (element) element.scrollIntoView({ behavior: 'smooth' });
          }}
          className="inline-flex items-center space-x-2 bg-green-700 hover:bg-green-800 text-white px-5 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 shadow hover:shadow-lg"
        >
          <span>Request Quote</span>
          <ArrowRight size={16} />
        </button>
      </div>
    </div>
  );
};

const Products = () => {
  return (
    <section id="products" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-orange-500 font-semibold text-sm uppercase tracking-wider">Our Products</span>
          <h2 className="text-4xl md:text-5xl font-bold text-amber-950 mt-3 mb-4">
            Premium Agricultural Products
          </h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            We export a wide range of high-quality agricultural products sourced directly from certified farms across India.
          </p>
        </div>

        {/* Products Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} />
          ))}
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <p className="text-gray-600 mb-6 text-lg">
            Looking for a specific product? We can source it for you!
          </p>
          <button 
            onClick={() => {
              const element = document.querySelector('#contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all duration-300 shadow-lg hover:shadow-xl inline-flex items-center space-x-2"
          >
            <span>Request Custom Quote</span>
            <ArrowRight size={20} />
          </button>
        </div>
      </div>
    </section>
  );
};

export default Products;
